// src/pages/Amostras/AnaliseFormModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { presetDiluicaoTorre } from '../../utils/diluicao';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    CircularProgress,
    Stack,
    Typography,
    IconButton,
    Paper,
    InputAdornment,
    Autocomplete,
    Alert,
    Divider,
    useMediaQuery,
    useTheme,
    Box,
    Chip,
    Slider,
    Collapse,
    Checkbox,
    Snackbar,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as Lucide from 'lucide-react';

import { getTowers } from '../../infra/api/Torres';
import { useDebounce } from '../../hooks/useDebounce';
import { useBootstrap } from '../../context/BootstrapContext';

const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch (e) {}
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
};

const resolveTowerAnalysisId = (tower) => {
    const candidates = [
        tower?.analiseId,
        tower?.analysisId,
        tower?.analise?.id,
        tower?.analise?._id,
        tower?.analysis?.id,
        tower?.analysis?._id,
    ];
    return candidates.map((value) => normalizeId(value)).find(Boolean) || '';
};

const normalizeProtocoloAnaliseRef = (ref) => {
    if (!ref || typeof ref !== 'object') return null;
    const protocoloId = normalizeId(ref.protocoloId);
    const analiseId = normalizeId(ref.analiseId);
    const protocoloNome = String(ref.protocoloNome || '').trim();
    const analiseNome = String(ref.analiseNome || '').trim();
    if (!protocoloId && !analiseId && !protocoloNome && !analiseNome) {
        return null;
    }
    return {
        ...(protocoloId ? { protocoloId } : {}),
        ...(protocoloNome ? { protocoloNome } : {}),
        ...(analiseId ? { analiseId } : {}),
        ...(analiseNome ? { analiseNome } : {}),
    };
};

const extractProtocolosAnalises = (source) => {
    if (!source || !Array.isArray(source.protocolosAnalises)) return [];
    return source.protocolosAnalises
        .map((item) => normalizeProtocoloAnaliseRef(item))
        .filter(Boolean);
};

const firstProtocoloAnalise = (source) =>
    extractProtocolosAnalises(source)[0] || null;

const upper = (v) =>
    String(v || '')
        .toUpperCase()
        .trim();

const isTowerLinked = (tower) => {
    const s = upper(tower?.status);
    return s === 'EM_PROCESSAMENTO' || s === 'ENCERRADA';
};

const parseNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const buildLocationSummary = (raw = {}) => {
    const neighborhood = String(raw.neighborhood || raw.district || '').trim();
    const city = String(raw.city || raw.locality || raw.town || '').trim();
    const state = String(raw.state || raw.region || '').trim();
    const zipCode = String(
        raw.zipCode || raw.postcode || raw.postalCode || ''
    ).trim();
    const formatted = [neighborhood, city, state].filter(Boolean).join(', ');

    return {
        neighborhood,
        city,
        state,
        zipCode,
        formatted:
            formatted ||
            [city, state].filter(Boolean).join(' - ') ||
            'Localizacao confirmada',
    };
};

const composeLocationQuery = (client = {}) =>
    [
        client.street,
        client.number,
        client.neighborhood,
        client.city,
        client.state,
        client.zipCode,
    ]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(', ');

const fetchLocalWeather = async (lat, lon) => {
    try {
        const url1 = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`;
        const res1 = await fetch(url1);
        if (res1.ok) {
            const data1 = await res1.json();
            if (data1?.current_weather?.temperature !== undefined) {
                return data1.current_weather.temperature;
            }
        }
    } catch (e) {
        console.warn('Open-Meteo falhou, tentando fallback wttr.in...');
    }
    try {
        const url2 = `https://wttr.in/${lat},${lon}?format=j1`;
        const res2 = await fetch(url2);
        if (res2.ok) {
            const data2 = await res2.json();
            if (data2?.current_condition?.[0]?.temp_C !== undefined) {
                return parseFloat(data2.current_condition[0].temp_C);
            }
        }
    } catch (e) {
        console.error('Ambas as APIs de clima falharam:', e);
    }
    return null;
};

const fetchAddressReverse = async (lat, lon) => {
    let summary = buildLocationSummary();
    try {
        const urlBDC = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=pt`;
        const res = await fetch(urlBDC);
        if (res.ok) {
            const data = await res.json();
            summary = buildLocationSummary({
                neighborhood:
                    data.localityInfo?.informative?.[0]?.name || data.locality,
                city: data.city || data.locality,
                state: data.principalSubdivision,
                zipCode: data.postcode,
            });
        }
    } catch (e) {}

    try {
        const urlNominatim = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&addressdetails=1`;
        const res = await fetch(urlNominatim, {
            headers: { 'Accept-Language': 'pt-BR' },
        });
        if (res.ok) {
            const data = await res.json();
            const addr = data?.address || {};
            const nominatimSummary = buildLocationSummary({
                neighborhood:
                    addr.suburb ||
                    addr.neighbourhood ||
                    addr.quarter ||
                    addr.city_district ||
                    addr.hamlet ||
                    '',
                city:
                    addr.city ||
                    addr.town ||
                    addr.village ||
                    addr.municipality ||
                    '',
                state: addr.state || addr.region || '',
                zipCode: addr.postcode || '',
            });

            summary = buildLocationSummary({
                neighborhood:
                    nominatimSummary.neighborhood || summary.neighborhood,
                city: nominatimSummary.city || summary.city,
                state: nominatimSummary.state || summary.state,
                zipCode: nominatimSummary.zipCode || summary.zipCode,
            });
        }
    } catch (e) {}

    return summary;
};

const searchAddressForward = async (query) => {
    try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&limit=1`;
        const res = await fetch(url, {
            headers: { 'Accept-Language': 'pt-BR' },
        });
        if (res.ok) {
            const data = await res.json();
            if (data && data.length > 0) {
                return {
                    latitude: parseFloat(data[0].lat),
                    longitude: parseFloat(data[0].lon),
                };
            }
        }
    } catch (e) {}

    try {
        const urlPhoton = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=1`;
        const res = await fetch(urlPhoton);
        if (res.ok) {
            const data = await res.json();
            if (data.features && data.features.length > 0) {
                const [lon, lat] = data.features[0].geometry.coordinates;
                return { latitude: lat, longitude: lon };
            }
        }
    } catch (e) {}

    return null;
};

const searchAddressForwardList = async (query) => {
    try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&addressdetails=1&limit=5`;
        const res = await fetch(url, {
            headers: { 'Accept-Language': 'pt-BR' },
        });
        if (!res.ok) throw new Error('Falha na busca');
        const data = await res.json();
        return data.map((item) => {
            const addr = item.address || {};
            const city =
                addr.city ||
                addr.town ||
                addr.village ||
                addr.municipality ||
                '';
            const state = addr.state || addr.region || '';
            const neighborhood =
                addr.suburb || addr.neighbourhood || addr.quarter || '';
            const zipCode = addr.postcode || '';
            const countryCode = addr.country_code
                ? addr.country_code.toUpperCase()
                : '';
            let label = [neighborhood, city, state, zipCode || countryCode]
                .filter(Boolean)
                .join(', ');
            if (!neighborhood && city)
                label = `${city} - ${state}, ${countryCode}`;

            return {
                lat: parseFloat(item.lat),
                lon: parseFloat(item.lon),
                displayName: item.display_name,
                label,
                city,
                state,
                neighborhood,
                zipCode,
                countryCode,
            };
        });
    } catch (e) {
        return [];
    }
};

const SearchLocationDialog = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setQuery('');
            setResults([]);
        }
    }, [open]);

    const handleSearch = async () => {
        if (!query.trim()) return;
        setLoading(true);
        const res = await searchAddressForwardList(query);
        setResults(res);
        setLoading(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    position: { xs: 'absolute', sm: 'relative' },
                    top: { xs: 16, sm: 'auto' },
                    m: 2,
                    width: { xs: 'calc(100% - 32px)', sm: '100%' },
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1.5,
                }}
            >
                <Lucide.Search size={18} />
                <Typography
                    variant="subtitle1"
                    sx={{ flexGrow: 1, fontWeight: 600 }}
                >
                    Buscar Endereço
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: 'white' }}
                >
                    <Lucide.X size={18} />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Digite cidade..."
                        placeholder="Ex: Santa Maria"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSearch();
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleSearch}
                                        disabled={loading || !query.trim()}
                                        edge="end"
                                        size="small"
                                    >
                                        {loading ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Lucide.Search />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {results.length > 0 && (
                        <List
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                maxHeight: '250px',
                                overflow: 'auto',
                                bgcolor: 'background.paper',
                            }}
                        >
                            {results.map((item, idx) => (
                                <ListItemButton
                                    key={idx}
                                    onClick={() => onSelect(item)}
                                    sx={{ py: 1 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Lucide.MapPin
                                            color={theme.palette.error.main}
                                            size={18}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                            >
                                                {item.city}{' '}
                                                {item.state
                                                    ? `- ${item.state}`
                                                    : ''}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                noWrap
                                            >
                                                {item.label}
                                            </Typography>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

const DilutionInput = ({ value, onChange, readOnly }) => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [sliderVal, setSliderVal] = useState(Number(value) || 0);

    useEffect(() => {
        if (!isEditing) setSliderVal(Number(value) || 0);
    }, [value, isEditing]);

    const handleChangeCommitted = (_, newVal) => {
        onChange(newVal);
        setIsEditing(false);
    };

    const hasValue = Number(value) > 0;

    const marks = [
        { value: 0, label: '0' },
        { value: 1, label: '-1' },
        { value: 2, label: '-2' },
        { value: 3, label: '-3' },
        { value: 4, label: '-4' },
        { value: 5, label: '-5' },
        { value: 6, label: '-6' },
    ];

    const sliderDisplay =
        sliderVal > 0 ? (
            <span>
                10<sup style={{ fontSize: '0.85em' }}>-{sliderVal}</sup>
            </span>
        ) : (
            'Pura (0)'
        );

    return (
        <Box sx={{ width: '100%' }}>
            <Button
                variant={hasValue ? 'contained' : 'outlined'}
                onClick={() => !readOnly && setIsEditing((prev) => !prev)}
                disabled={readOnly}
                startIcon={<Lucide.Droplet size={18} />}
                fullWidth
                sx={{
                    justifyContent: 'center',
                    height: 40,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: hasValue ? '1rem' : '0.9rem',
                    ...(hasValue
                        ? {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.dark',
                              borderColor: 'transparent',
                              '&:hover': {
                                  bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.2
                                  ),
                                  borderColor: 'transparent',
                              },
                          }
                        : {
                              color: 'text.secondary',
                              borderColor: 'divider',
                              '&:hover': {
                                  bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.04
                                  ),
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                              },
                          }),
                }}
            >
                {hasValue ? (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        Diluição: 10
                        <sup style={{ marginLeft: 2, fontSize: '0.85em' }}>
                            -{value}
                        </sup>
                    </Box>
                ) : (
                    'Pura (Sem diluição)'
                )}
            </Button>

            <Collapse in={isEditing} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 2,
                        mt: 1,
                        bgcolor: 'background.default',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        boxShadow: theme.shadows[1],
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1.5}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="primary"
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            Ajustar:{' '}
                            <span style={{ marginLeft: 6, fontSize: '1.1em' }}>
                                {sliderDisplay}
                            </span>
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setIsEditing(false)}
                            sx={{ p: 0.5, mr: -1 }}
                        >
                            <Lucide.X size={18} />
                        </IconButton>
                    </Stack>
                    <Box sx={{ px: 1.5 }}>
                        <Slider
                            value={sliderVal}
                            onChange={(_, v) => setSliderVal(v)}
                            onChangeCommitted={handleChangeCommitted}
                            min={0}
                            max={6}
                            step={1}
                            marks={marks}
                            disabled={readOnly}
                            sx={{
                                '& .MuiSlider-markLabel': {
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

const AnaliseFormModal = ({
    open,
    onClose,
    onSubmit,
    initialValue,
    mode = 'create',
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isEdit = mode === 'edit';
    const isView = mode === 'view';

    const { bootstrap } = useBootstrap();
    const protocolos = useMemo(
        () => bootstrap?.protocolos || bootstrap?.protocols || [],
        [bootstrap]
    );

    const [location, setLocation] = useState(null);
    const [ambientTemperature, setAmbientTemperature] = useState('');
    const [tempLoading, setTempLoading] = useState(false);

    const [identificacao, setIdentificacao] = useState('');
    const [protocoloId, setProtocoloId] = useState('');
    const [analiseId, setAnaliseId] = useState('');
    const [towerQueryAfluente, setTowerQueryAfluente] = useState('');
    const [towerQueryEfluente, setTowerQueryEfluente] = useState('');
    const debouncedTowerQueryAfluente = useDebounce(towerQueryAfluente, 400);
    const debouncedTowerQueryEfluente = useDebounce(towerQueryEfluente, 400);
    const [towerLoadingAfluente, setTowerLoadingAfluente] = useState(false);
    const [towerLoadingEfluente, setTowerLoadingEfluente] = useState(false);
    const [towerOptionsAfluente, setTowerOptionsAfluente] = useState([]);
    const [towerOptionsEfluente, setTowerOptionsEfluente] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [hydratingInitial, setHydratingInitial] = useState(false);

    const locationDetails = useMemo(() => {
        return buildLocationSummary({
            neighborhood: bootstrap?.client?.neighborhood,
            city: bootstrap?.client?.city,
            state: bootstrap?.client?.state,
            zipCode: bootstrap?.client?.zipCode,
        });
    }, [bootstrap]);

    const locationDisplayName = [
        locationDetails?.city,
        locationDetails?.neighborhood,
    ]
        .filter(Boolean)
        .join(' - ');

    const hasConfiguredLocation = Boolean(locationDisplayName);

    const specialTreatmentMode = useMemo(() => {
        if (protocolos.length !== 1) return null;
        const proto = protocolos[0];
        const analises = proto.analises || proto.analysis || [];
        const afluente = analises.find((a) =>
            (a.name || a.nome).toLowerCase().includes('afluente')
        );
        const efluente = analises.find((a) =>
            (a.name || a.nome).toLowerCase().includes('efluente')
        );
        if (afluente && efluente && analises.length === 2) {
            return {
                protocolo: proto,
                analiseAfluente: afluente,
                analiseEfluente: efluente,
            };
        }
        return null;
    }, [protocolos]);

    const specialTreatmentAfluenteId = useMemo(
        () =>
            normalizeId(
                specialTreatmentMode?.analiseAfluente?.id ||
                    specialTreatmentMode?.analiseAfluente?._id
            ),
        [specialTreatmentMode]
    );
    const specialTreatmentEfluenteId = useMemo(
        () =>
            normalizeId(
                specialTreatmentMode?.analiseEfluente?.id ||
                    specialTreatmentMode?.analiseEfluente?._id
            ),
        [specialTreatmentMode]
    );
    const specialTreatmentAfluenteLabel = useMemo(
        () =>
            String(
                specialTreatmentMode?.analiseAfluente?.name ||
                    specialTreatmentMode?.analiseAfluente?.nome ||
                    'Afluente'
            ).trim(),
        [specialTreatmentMode]
    );
    const specialTreatmentEfluenteLabel = useMemo(
        () =>
            String(
                specialTreatmentMode?.analiseEfluente?.name ||
                    specialTreatmentMode?.analiseEfluente?.nome ||
                    'Efluente'
            ).trim(),
        [specialTreatmentMode]
    );

    const editLocked = useMemo(() => {
        if (!isEdit) return false;
        const amostraStatus = upper(initialValue?.status);
        if (amostraStatus && amostraStatus !== 'PENDENTE') return true;
        const torres = Array.isArray(initialValue?.torres)
            ? initialValue.torres
            : [];
        return torres.some(isTowerLinked);
    }, [isEdit, initialValue]);
    const readOnly = isView || editLocked;

    const selectedTowerIds = useMemo(
        () =>
            new Set(
                selectedItems.map((x) =>
                    normalizeId(x?.tower?.id || x?.tower?._id)
                )
            ),
        [selectedItems]
    );

    const resolveSpecialGroup = (item) => {
        const explicit = String(item?.specialTreatmentGroup || '').trim();
        if (explicit) return explicit;
        const analysisId = resolveTowerAnalysisId(item?.tower);
        if (analysisId && analysisId === specialTreatmentAfluenteId) {
            return 'afluente';
        }
        if (analysisId && analysisId === specialTreatmentEfluenteId) {
            return 'efluente';
        }
        return '';
    };

    useEffect(() => {
        if (specialTreatmentMode && !protocoloId && !initialValue) {
            const pId = normalizeId(
                specialTreatmentMode.protocolo.id ||
                    specialTreatmentMode.protocolo._id
            );
            setProtocoloId(pId);
            setAnaliseId('');
        }
    }, [specialTreatmentMode, protocoloId, initialValue]);

    const applySelection = (newItems) => {
        setSelectedItems(newItems);
    };

    const addTower = (tower, specialTreatmentGroup = '') => {
        if (readOnly) return;
        const id = normalizeId(tower?.id || tower?._id);
        if (!id) return;
        if (selectedTowerIds.has(id)) return;
        // Preset do dispositivo como valor inicial. Precisa virar STRING: o handleSubmit
        // so inclui diluicao no payload quando String(...).trim() !== ''.
        const preset = presetDiluicaoTorre(tower);
        applySelection([
            ...selectedItems,
            {
                tower,
                diluicao: preset === null ? '' : String(preset),
                specialTreatmentGroup,
            },
        ]);
    };

    const removeTower = (towerId) => {
        if (readOnly) return;
        applySelection(
            selectedItems.filter(
                (x) => normalizeId(x?.tower?.id || x?.tower?._id) !== towerId
            )
        );
    };

    const updateDiluicao = (towerId, value) => {
        if (readOnly) return;
        const next = selectedItems.map((x) => {
            const id = normalizeId(x?.tower?.id || x?.tower?._id);
            if (id !== towerId) return x;
            return { ...x, diluicao: value };
        });
        setSelectedItems(next);
    };

    useEffect(() => {
        if (!open) return;

        if (initialValue?.identificacao) {
            setIdentificacao(initialValue.identificacao);
        } else {
            setIdentificacao('');
        }

        const principalProtocoloAnalise = firstProtocoloAnalise(initialValue);
        setProtocoloId(principalProtocoloAnalise?.protocoloId || '');
        setAnaliseId('');

        const tArr = Array.isArray(initialValue?.torres)
            ? initialValue.torres
            : [];
        setSelectedItems(
            tArr
                .filter((t) => t.torreId || t.numeroSerie)
                .map((t) => {
                    const tower = {
                        id: t.torreId,
                        numeroSerie: t.numeroSerie,
                        status: t.status,
                        tag: t.tag,
                        tagColor: t.tagColor,
                        analiseId: t.analiseId || t.analysisId,
                        analise: t.analise || t.analysis,
                    };
                    const analysisId = resolveTowerAnalysisId({
                        ...t,
                        ...tower,
                    });
                    const specialTreatmentGroup =
                        analysisId && analysisId === specialTreatmentAfluenteId
                            ? 'afluente'
                            : analysisId &&
                                analysisId === specialTreatmentEfluenteId
                              ? 'efluente'
                              : '';
                    return {
                        tower,
                        diluicao: t.diluicao ?? '',
                        specialTreatmentGroup,
                    };
                })
        );
        setTowerQueryAfluente('');
        setTowerQueryEfluente('');
        setTowerOptionsAfluente([]);
        setTowerOptionsEfluente([]);
        setHydratingInitial(false);

        const initialLat = parseNumber(initialValue?.localizacao?.latitude);
        const initialLon = parseNumber(initialValue?.localizacao?.longitude);

        if (
            initialValue?.temperaturaAmostra !== undefined &&
            initialValue?.temperaturaAmostra !== null
        ) {
            setAmbientTemperature(String(initialValue.temperaturaAmostra));
            setLocation(
                initialLat !== null && initialLon !== null
                    ? initialValue.localizacao
                    : null
            );
        } else if (
            mode === 'create' &&
            hasConfiguredLocation &&
            bootstrap?.client
        ) {
            const fullQuery = composeLocationQuery(bootstrap.client);
            if (fullQuery) {
                setTempLoading(true);
                searchAddressForward(fullQuery).then(async (coords) => {
                    if (coords) {
                        setLocation({
                            latitude: coords.latitude,
                            longitude: coords.longitude,
                        });
                        const temp = await fetchLocalWeather(
                            coords.latitude,
                            coords.longitude
                        );
                        if (temp !== null) {
                            setAmbientTemperature(String(temp));
                        }
                    }
                    setTempLoading(false);
                });
            } else {
                setAmbientTemperature('');
                setLocation(null);
            }
        } else {
            setAmbientTemperature('');
            setLocation(null);
        }
    }, [
        open,
        initialValue,
        bootstrap,
        mode,
        specialTreatmentAfluenteId,
        specialTreatmentEfluenteId,
        hasConfiguredLocation,
    ]);

    useEffect(() => {
        if (!open) return;
        if (!(isEdit || isView)) return;
        const torresInicial = Array.isArray(initialValue?.torres)
            ? initialValue.torres
            : [];
        const serials = torresInicial
            .map((t) => String(t?.numeroSerie || '').trim())
            .filter(Boolean);
        if (serials.length === 0) return;
        let cancelled = false;
        const hydrate = async () => {
            setHydratingInitial(true);
            try {
                const results = await Promise.all(
                    serials.map(async (s) => {
                        try {
                            const page = await getTowers(0, 10, {
                                numeroSerie: s,
                            });
                            const list = Array.isArray(page?.content)
                                ? page.content
                                : [];
                            return (
                                (Array.isArray(list) &&
                                    list.find(
                                        (x) =>
                                            String(x?.numeroSerie || '')
                                                .trim()
                                                .toUpperCase() ===
                                            s.toUpperCase()
                                    )) ||
                                null
                            );
                        } catch {
                            return null;
                        }
                    })
                );
                if (cancelled) return;
                const map = new Map();
                results.filter(Boolean).forEach((t) => {
                    map.set(
                        String(t?.numeroSerie || '')
                            .trim()
                            .toUpperCase(),
                        t
                    );
                });
                setSelectedItems((prev) => {
                    const next = prev.map((item) => {
                        const serial = String(item?.tower?.numeroSerie || '')
                            .trim()
                            .toUpperCase();
                        const full = map.get(serial);
                        if (!full) return item;
                        const analysisId = resolveTowerAnalysisId(full);
                        const inferredGroup =
                            item?.specialTreatmentGroup ||
                            (analysisId &&
                            analysisId === specialTreatmentAfluenteId
                                ? 'afluente'
                                : analysisId &&
                                    analysisId === specialTreatmentEfluenteId
                                  ? 'efluente'
                                  : '');
                        return {
                            ...item,
                            specialTreatmentGroup: inferredGroup,
                            tower: {
                                ...full,
                                status: item?.tower?.status ?? full?.status,
                            },
                        };
                    });
                    return next;
                });
            } finally {
                if (!cancelled) setHydratingInitial(false);
            }
        };
        hydrate();
        return () => {
            cancelled = true;
        };
    }, [
        open,
        isEdit,
        isView,
        initialValue,
        specialTreatmentAfluenteId,
        specialTreatmentEfluenteId,
    ]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        const run = async () => {
            const pid = normalizeId(protocoloId);
            if (specialTreatmentMode && pid) {
                setTowerLoadingAfluente(true);
                setTowerLoadingEfluente(true);
                try {
                    const [pageAfluente, pageEfluente] = await Promise.all([
                        specialTreatmentAfluenteId
                            ? getTowers(0, 50, {
                                  protocoloId: pid,
                                  analiseId: specialTreatmentAfluenteId,
                                  running: false,
                                  ...(debouncedTowerQueryAfluente?.trim()
                                      ? {
                                            numeroSerie:
                                                debouncedTowerQueryAfluente.trim(),
                                        }
                                      : {}),
                              })
                            : Promise.resolve({ content: [] }),
                        specialTreatmentEfluenteId
                            ? getTowers(0, 50, {
                                  protocoloId: pid,
                                  analiseId: specialTreatmentEfluenteId,
                                  running: false,
                                  ...(debouncedTowerQueryEfluente?.trim()
                                      ? {
                                            numeroSerie:
                                                debouncedTowerQueryEfluente.trim(),
                                        }
                                      : {}),
                              })
                            : Promise.resolve({ content: [] }),
                    ]);
                    if (cancelled) return;
                    setTowerOptionsAfluente(pageAfluente?.content || []);
                    setTowerOptionsEfluente(pageEfluente?.content || []);
                } catch {
                    if (cancelled) return;
                    setTowerOptionsAfluente([]);
                    setTowerOptionsEfluente([]);
                } finally {
                    if (cancelled) return;
                    setTowerLoadingAfluente(false);
                    setTowerLoadingEfluente(false);
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [
        open,
        debouncedTowerQueryAfluente,
        debouncedTowerQueryEfluente,
        protocoloId,
        specialTreatmentMode,
        specialTreatmentAfluenteId,
        specialTreatmentEfluenteId,
    ]);

    const canSubmit = useMemo(() => {
        if (readOnly) return false;
        if (!identificacao.trim()) return false;
        if (!normalizeId(protocoloId)) return false;
        if (!selectedItems.length) return false;
        return true;
    }, [readOnly, identificacao, protocoloId, selectedItems]);

    const handleSubmit = () => {
        if (readOnly) return;

        const torres = selectedItems.map((x) => {
            const towerId = normalizeId(x?.tower?.id || x?.tower?._id);
            const item = { torreId: towerId };
            const d = String(x?.diluicao ?? '').trim();
            if (d !== '') item.diluicao = d;
            return item;
        });

        let finalSampleTemp = null;
        const rawTemp = String(ambientTemperature).trim();
        if (rawTemp !== '') {
            const sanitized = rawTemp.replace(',', '.');
            const parsed = parseFloat(sanitized);
            if (!isNaN(parsed)) {
                finalSampleTemp = parsed;
            }
        }

        const payload = {
            identificacao: identificacao.trim(),
            protocolosAnalises: (() => {
                const pid = normalizeId(protocoloId);
                const protocoloNomeSelecionado = String(
                    specialTreatmentMode?.protocolo?.nome ||
                        specialTreatmentMode?.protocolo?.name ||
                        ''
                ).trim();

                if (!pid) return [];

                if (specialTreatmentMode) {
                    return [
                        {
                            protocoloId: pid,
                            protocoloNome:
                                protocoloNomeSelecionado || undefined,
                            analiseId: specialTreatmentAfluenteId || undefined,
                            analiseNome: 'Afluente',
                        },
                        {
                            protocoloId: pid,
                            protocoloNome:
                                protocoloNomeSelecionado || undefined,
                            analiseId: specialTreatmentEfluenteId || undefined,
                            analiseNome: 'Efluente',
                        },
                    ]
                        .map((item) => normalizeProtocoloAnaliseRef(item))
                        .filter(Boolean);
                }
                return [];
            })(),
            torres,
            localizacao: location || undefined,
            temperaturaAmostra: finalSampleTemp,
        };
        onSubmit?.(payload);
    };

    const title = useMemo(() => {
        if (mode === 'view') return 'Detalhes da Análise';
        if (mode === 'edit') return 'Editar Análise';
        return 'Nova Análise';
    }, [mode]);

    const icon = <Lucide.Square size={18} />;
    const checkedIcon = <Lucide.SquareCheck size={18} />;

    const renderTowerOption = (props, option) => {
        const { key, ...otherProps } = props;
        const isSelected = selectedTowerIds.has(
            normalizeId(option.id || option._id)
        );
        return (
            <li key={key} {...otherProps} style={{ alignItems: 'flex-start' }}>
                <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8, padding: 4, marginTop: 4 }}
                    checked={isSelected}
                    disabled={isSelected}
                />
                <Box sx={{ pt: 0.5 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Typography variant="body2" fontWeight={700}>
                            SN: {option.numeroSerie}
                        </Typography>
                        {option.tag && (
                            <Chip
                                label={option.tag}
                                size="small"
                                sx={{
                                    bgcolor: option.tagColor || '#9e9e9e',
                                    color: '#fff',
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                }}
                            />
                        )}
                    </Stack>
                </Box>
            </li>
        );
    };

    const renderTowerAutocomplete = ({
        options,
        inputValue,
        onInputChange,
        onSelect,
        label,
        placeholder,
        loading,
        disabled,
        colorType = 'primary',
    }) => (
        <Autocomplete
            options={options}
            value={null}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                onInputChange(newInputValue);
            }}
            onChange={(event, tower) => {
                if (tower && !readOnly) onSelect(tower);
            }}
            getOptionLabel={(t) => `${t?.numeroSerie || ''}`}
            renderOption={renderTowerOption}
            loading={loading}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    size="small"
                    label={label}
                    placeholder={placeholder}
                    color={colorType}
                    sx={{
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                            {
                                borderColor: `${colorType}.main`,
                            },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: `${colorType}.main`,
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lucide.Search color={theme.palette[colorType]?.main} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <>
                                {loading ? (
                                    <CircularProgress
                                        color={colorType}
                                        size={18}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
        >
            <DialogTitle
                sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: isMobile ? 1.5 : 2,
                }}
            >
                <Lucide.FlaskConical />
                <Typography
                    variant={isMobile ? 'subtitle1' : 'h6'}
                    component="div"
                    sx={{
                        flexGrow: 1,
                        fontWeight: 600,
                        fontSize: isMobile ? '1rem' : '1.25rem',
                    }}
                >
                    {title}
                </Typography>
                <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
                    <Lucide.X />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{ bgcolor: 'background.default', p: isMobile ? 2 : 3 }}
            >
                <Stack spacing={3}>
                    {editLocked && (
                        <Alert severity="info" icon={<Lucide.ClipboardCheck />}>
                            Esta análise não pode ser editada pois já possui
                            vínculo com experimentos.
                        </Alert>
                    )}

                    {/* PASSO 1: IDENTIFICAÇÃO DA ANÁLISE */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            fontWeight={700}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Lucide.Tag size={18} /> 1. Identificação da
                            Análise
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nome/Referência"
                            value={identificacao}
                            onChange={(e) => setIdentificacao(e.target.value)}
                            required
                            disabled={readOnly}
                            placeholder="Ex: Lote 123 - Tanque A"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lucide.QrCode color={theme.palette.action.active} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Paper>

                    {/* PASSO 2: TEMPERATURA DA AMOSTRA */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            fontWeight={700}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Lucide.Thermometer size={18} /> 2. Temperatura
                            da Amostra
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            {hasConfiguredLocation ? (
                                <>
                                    Temperatura sugerida para a região de{' '}
                                    <strong>{locationDisplayName}</strong>.
                                    Ajuste conforme a medição real da amostra.
                                </>
                            ) : (
                                <>
                                    Insira a temperatura da amostra no momento
                                    da recolha.
                                </>
                            )}
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            label="Temperatura (°C)"
                            value={ambientTemperature}
                            onChange={(e) =>
                                setAmbientTemperature(e.target.value)
                            }
                            placeholder="Ex: 25.5"
                            type="text"
                            disabled={readOnly || tempLoading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lucide.Thermometer color={theme.palette.action.active} />
                                    </InputAdornment>
                                ),
                                endAdornment: tempLoading && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ),
                            }}
                            helperText={
                                tempLoading
                                    ? 'Consultando temperatura local...'
                                    : ''
                            }
                        />
                    </Paper>

                    {/* PASSO 3: VINCULAÇÃO DE DISPOSITIVOS */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            fontWeight={700}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <Lucide.Shapes size={18} /> 3. Vinculação de
                            Dispositivos (BioAILabs)
                        </Typography>

                        {specialTreatmentMode && (
                            <Stack spacing={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                height: '100%',
                                                bgcolor: alpha(
                                                    theme.palette.error.main,
                                                    0.03
                                                ),
                                                borderColor: alpha(
                                                    theme.palette.error.main,
                                                    0.2
                                                ),
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={700}
                                                    color="error.main"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    Entrada (Afluente)
                                                </Typography>
                                                {renderTowerAutocomplete({
                                                    options:
                                                        towerOptionsAfluente,
                                                    inputValue:
                                                        towerQueryAfluente,
                                                    onInputChange:
                                                        setTowerQueryAfluente,
                                                    onSelect: (tower) => {
                                                        addTower(
                                                            tower,
                                                            'afluente'
                                                        );
                                                        setTowerQueryAfluente(
                                                            ''
                                                        );
                                                    },
                                                    label: `Buscar BioAILab...`,
                                                    placeholder:
                                                        'Serial ou tag...',
                                                    loading:
                                                        towerLoadingAfluente,
                                                    disabled: readOnly,
                                                    colorType: 'error',
                                                })}
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                height: '100%',
                                                bgcolor: alpha(
                                                    theme.palette.info.main,
                                                    0.03
                                                ),
                                                borderColor: alpha(
                                                    theme.palette.info.main,
                                                    0.2
                                                ),
                                            }}
                                        >
                                            <Stack spacing={1}>
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight={700}
                                                    color="info.main"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    Saída (Efluente)
                                                </Typography>
                                                {renderTowerAutocomplete({
                                                    options:
                                                        towerOptionsEfluente,
                                                    inputValue:
                                                        towerQueryEfluente,
                                                    onInputChange:
                                                        setTowerQueryEfluente,
                                                    onSelect: (tower) => {
                                                        addTower(
                                                            tower,
                                                            'efluente'
                                                        );
                                                        setTowerQueryEfluente(
                                                            ''
                                                        );
                                                    },
                                                    label: `Buscar BioAILab...`,
                                                    placeholder:
                                                        'Serial ou tag...',
                                                    loading:
                                                        towerLoadingEfluente,
                                                    disabled: readOnly,
                                                    colorType: 'info',
                                                })}
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Stack>
                        )}

                        {hydratingInitial && (
                            <CircularProgress
                                size={20}
                                sx={{ alignSelf: 'center', mt: 2 }}
                            />
                        )}

                        <Divider sx={{ my: 2 }}>
                            <Chip
                                label={`Selecionados: ${selectedItems.length}`}
                                size="small"
                            />
                        </Divider>

                        {selectedItems.length === 0 ? (
                            <Box
                                sx={{
                                    py: 3,
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                    border: '1px dashed grey',
                                    borderRadius: 2,
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Lucide.CirclePlus
                                    size={40}
                                    style={{ opacity: 0.3, marginBottom: 8 }}
                                />
                                <Typography variant="body2">
                                    Nenhum dispositivo vinculado ainda. Busque e
                                    adicione acima.
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={2}>
                                {selectedItems.map((x) => {
                                    const t = x.tower;
                                    const id = normalizeId(t?.id || t?._id);
                                    const serial = String(
                                        t?.numeroSerie ?? ''
                                    ).trim();
                                    const specialGroupKey = specialTreatmentMode
                                        ? resolveSpecialGroup(x)
                                        : '';

                                    let borderColor = 'divider';
                                    let bgColor = 'background.paper';
                                    let chipColor = 'default';
                                    let groupTitle = '';

                                    if (specialGroupKey === 'afluente') {
                                        borderColor = alpha(
                                            theme.palette.error.main,
                                            0.4
                                        );
                                        bgColor = alpha(
                                            theme.palette.error.main,
                                            0.02
                                        );
                                        chipColor = 'error';
                                        groupTitle = 'Entrada (Afluente)';
                                    } else if (specialGroupKey === 'efluente') {
                                        borderColor = alpha(
                                            theme.palette.info.main,
                                            0.4
                                        );
                                        bgColor = alpha(
                                            theme.palette.info.main,
                                            0.02
                                        );
                                        chipColor = 'info';
                                        groupTitle = 'Saída (Efluente)';
                                    }

                                    return (
                                        <Paper
                                            key={id || serial}
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                bgcolor: bgColor,
                                                border: '1px solid',
                                                borderColor: borderColor,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            <Box>
                                                {specialTreatmentMode &&
                                                    groupTitle && (
                                                        <Typography
                                                            variant="caption"
                                                            fontWeight={800}
                                                            color={`${chipColor}.main`}
                                                            sx={{
                                                                textTransform:
                                                                    'uppercase',
                                                                mb: 0.5,
                                                                display:
                                                                    'block',
                                                            }}
                                                        >
                                                            {groupTitle}
                                                        </Typography>
                                                    )}

                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1.5}
                                                    flexWrap="wrap"
                                                    useFlexGap
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={800}
                                                        color="text.primary"
                                                    >
                                                        SN: {serial || 'Sem ID'}
                                                    </Typography>

                                                    {t?.tag && (
                                                        <Chip
                                                            label={t.tag}
                                                            size="small"
                                                            sx={{
                                                                bgcolor:
                                                                    t.tagColor ||
                                                                    '#9e9e9e',
                                                                color: '#fff',
                                                                fontWeight:
                                                                    'bold',
                                                                fontSize:
                                                                    '0.75rem',
                                                                height: 24,
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </Box>

                                            <Divider
                                                sx={{
                                                    borderStyle: 'dashed',
                                                    borderColor: borderColor,
                                                }}
                                            />

                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="flex-start"
                                            >
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <DilutionInput
                                                        value={x.diluicao}
                                                        onChange={(val) =>
                                                            updateDiluicao(
                                                                id ||
                                                                    normalizeId(
                                                                        t?.torreId
                                                                    ),
                                                                val
                                                            )
                                                        }
                                                        readOnly={readOnly}
                                                    />
                                                </Box>
                                                <IconButton
                                                    onClick={() =>
                                                        removeTower(
                                                            id ||
                                                                normalizeId(
                                                                    t?.torreId
                                                                )
                                                        )
                                                    }
                                                    color="error"
                                                    size="small"
                                                    disabled={readOnly}
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor:
                                                            'error.light',
                                                        bgcolor: 'error.50',
                                                        height: 40,
                                                        width: 40,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <Lucide.Trash2 size={18} />
                                                </IconButton>
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        )}
                    </Paper>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    flexDirection: isMobile ? 'column-reverse' : 'row',
                    gap: isMobile ? 2 : 1,
                }}
            >
                <Button
                    onClick={onClose}
                    variant="text"
                    color="inherit"
                    size={isMobile ? 'large' : 'small'}
                    fullWidth={isMobile}
                >
                    {readOnly ? 'Fechar' : 'Cancelar'}
                </Button>
                {!readOnly && (
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        variant="contained"
                        size={isMobile ? 'large' : 'small'}
                        fullWidth={isMobile}
                        startIcon={<Lucide.Save />}
                        disableElevation
                        sx={{ px: 4 }}
                    >
                        {mode === 'edit' ? 'Atualizar' : 'Salvar Análise'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AnaliseFormModal;
